import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import type { InspectionEvent } from "xstate";

/**
 * Checks if debug console logging is enabled via feature toggle
 * @returns True if ENABLE_DEBUG_CONSOLE_LOGS feature toggle is enabled
 */
function isDebugLoggingEnabled(): boolean {
  return isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DEBUG_CONSOLE_LOGS);
}

/**
 * Log categories with emoji prefixes for visual scanning
 */
const LogCategory = {
  STATE_CHANGE: "🔄",
  GUARD: "🔍",
  STATE_ENTRY: "📍",
  DECISION_STATE: "🔀",
  EVENT: "👤",
  DOCTOR_EVENT: "🏥",
  NURSE_EVENT: "🩺",
  FACE_SCAN: "📸",
  HEALTH_SYNC: "💚",
  TRANSITION: "➡️",
  ACTOR_START: "🚀",
  ACTOR_DONE: "✅",
  ACTOR_ERROR: "❌",
} as const;

type LogContext = Record<string, unknown>;

/**
 * Core debug logging function - only logs when feature toggle is enabled
 * Provides early return for zero overhead when disabled
 *
 * @param category - Log category emoji prefix
 * @param label - Descriptive label for the log entry
 * @param context - Context object with relevant data
 */
function debugLog(category: string, label: string, context?: LogContext): void {
  if (!isDebugLoggingEnabled()) {
    return;
  }

  if (context) {
    console.log(`${category} ${label}:`, context);
  } else {
    console.log(`${category} ${label}`);
  }
}

/** Creates a set of logging methods scoped to a specific machine name */
function createScopedLogger(machineName: string) {
  return {
    stateChange: (label: string, context: LogContext) => {
      debugLog(LogCategory.STATE_CHANGE, `[${machineName}] ${label}`, context);
    },

    guard: (guardName: string, context: LogContext) => {
      debugLog(
        LogCategory.GUARD,
        `[${machineName}:Guard] ${guardName}`,
        context
      );
    },

    stateEntry: (stateName: string, context: LogContext) => {
      debugLog(
        LogCategory.STATE_ENTRY,
        `[${machineName}:Entry] ${stateName}`,
        context
      );
    },

    decisionState: (stateName: string, context: LogContext) => {
      debugLog(
        LogCategory.DECISION_STATE,
        `[${machineName}:Decision] ${stateName}`,
        context
      );
    },

    event: (eventName: string, context: LogContext) => {
      debugLog(LogCategory.EVENT, `[${machineName}] ${eventName}`, context);
    },

    doctorEvent: (eventName: string, context: LogContext) => {
      debugLog(
        LogCategory.DOCTOR_EVENT,
        `[${machineName}] ${eventName}`,
        context
      );
    },

    nurseEvent: (eventName: string, context: LogContext) => {
      debugLog(
        LogCategory.NURSE_EVENT,
        `[${machineName}] ${eventName}`,
        context
      );
    },

    faceScan: (label: string, context: LogContext) => {
      debugLog(
        LogCategory.FACE_SCAN,
        `[${machineName}:FaceScan] ${label}`,
        context
      );
    },

    healthSync: (label: string, context?: LogContext) => {
      debugLog(
        LogCategory.HEALTH_SYNC,
        `[${machineName}:HealthDataSync] ${label}`,
        context
      );
    },

    transition: (
      from: string,
      to: string,
      event: string,
      context?: LogContext
    ) => {
      debugLog(
        LogCategory.TRANSITION,
        `[${machineName}] ${from} → ${to} (event: ${event})`,
        context
      );
    },

    actorStart: (actorId: string, context?: LogContext) => {
      debugLog(
        LogCategory.ACTOR_START,
        `[${machineName}:Actor] ${actorId} started`,
        context
      );
    },

    actorDone: (actorId: string, context?: LogContext) => {
      debugLog(
        LogCategory.ACTOR_DONE,
        `[${machineName}:Actor] ${actorId} done`,
        context
      );
    },

    actorError: (actorId: string, context?: LogContext) => {
      debugLog(
        LogCategory.ACTOR_ERROR,
        `[${machineName}:Actor] ${actorId} error`,
        context
      );
    },
  };
}

export const debugLogger = {
  stateChange: (label: string, context: LogContext) => {
    debugLog(
      LogCategory.STATE_CHANGE,
      `[IntercareMedicalAssistance] ${label}`,
      context
    );
  },

  guard: (guardName: string, context: LogContext) => {
    debugLog(LogCategory.GUARD, `[Guard] ${guardName}`, context);
  },

  stateEntry: (stateName: string, context: LogContext) => {
    debugLog(LogCategory.STATE_ENTRY, `[State Entry] ${stateName}`, context);
  },

  decisionState: (stateName: string, context: LogContext) => {
    debugLog(
      LogCategory.DECISION_STATE,
      `[Decision State] ${stateName}`,
      context
    );
  },

  event: (eventName: string, context: LogContext) => {
    debugLog(
      LogCategory.EVENT,
      `[IntercareMedicalAssistance] ${eventName}`,
      context
    );
  },

  doctorEvent: (eventName: string, context: LogContext) => {
    debugLog(
      LogCategory.DOCTOR_EVENT,
      `[IntercareMedicalAssistance] ${eventName}`,
      context
    );
  },

  nurseEvent: (eventName: string, context: LogContext) => {
    debugLog(
      LogCategory.NURSE_EVENT,
      `[IntercareMedicalAssistance] ${eventName}`,
      context
    );
  },

  faceScan: (label: string, context: LogContext) => {
    debugLog(LogCategory.FACE_SCAN, `[FaceScan] ${label}`, context);
  },

  healthSync: (label: string, context?: LogContext) => {
    debugLog(LogCategory.HEALTH_SYNC, `[HealthDataSync] ${label}`, context);
  },

  transition: (
    from: string,
    to: string,
    event: string,
    context?: LogContext
  ) => {
    debugLog(
      LogCategory.TRANSITION,
      `[Transition] ${from} → ${to} (event: ${event})`,
      context
    );
  },

  actorStart: (actorId: string, context?: LogContext) => {
    debugLog(LogCategory.ACTOR_START, `[Actor] ${actorId} started`, context);
  },

  actorDone: (actorId: string, context?: LogContext) => {
    debugLog(LogCategory.ACTOR_DONE, `[Actor] ${actorId} done`, context);
  },

  actorError: (actorId: string, context?: LogContext) => {
    debugLog(LogCategory.ACTOR_ERROR, `[Actor] ${actorId} error`, context);
  },

  /**
   * Creates a scoped logger with all methods prefixed by the given machine name.
   * Useful for reusing the logger across different state machines.
   *
   * @param machineName - Name used in log prefixes, e.g. "AuthFlow"
   * @returns Object with all debugLogger methods scoped to the machine name
   */
  forMachine: createScopedLogger,

  /**
   * Creates an XState inspect callback that automatically logs transitions,
   * events, and actor lifecycle for any state machine. Pass the returned
   * function to useMachine({ inspect }).
   *
   * @param machineName - Name used in log prefixes
   * @returns Inspect callback compatible with XState v5 useMachine options
   */
  createInspector: (machineName: string) => {
    const logger = createScopedLogger(machineName);
    let previousStateValue: string | undefined;

    return (inspectionEvent: InspectionEvent) => {
      if (!isDebugLoggingEnabled()) return;

      switch (inspectionEvent.type) {
        case "@xstate.snapshot": {
          const { snapshot, event, actorRef } = inspectionEvent;
          const actorId = String((actorRef as any).id ?? actorRef.sessionId);
          const currentValue = (snapshot as any).value;
          const isRootActor = actorId === inspectionEvent.rootId;

          if (
            isRootActor &&
            currentValue &&
            currentValue !== previousStateValue
          ) {
            logger.transition(
              String(previousStateValue ?? "initial"),
              String(currentValue),
              event.type
            );
            previousStateValue = currentValue;
          }

          if (!isRootActor) {
            const status = (snapshot as any).status;
            if (status === "done") {
              logger.actorDone(actorId, {
                output: (snapshot as any).output,
              });
            } else if (status === "error") {
              logger.actorError(actorId, {
                error: (snapshot as any).error,
              });
            }
          }
          break;
        }
        case "@xstate.event": {
          const { event, actorRef } = inspectionEvent;
          const actorId = String((actorRef as any).id ?? actorRef.sessionId);
          const { type, ...payload } = event;
          logger.event(type, { actorId, ...payload });
          break;
        }
        case "@xstate.actor": {
          const actorId = String(
            (inspectionEvent.actorRef as any).id ??
              inspectionEvent.actorRef.sessionId
          );
          logger.actorStart(actorId);
          break;
        }
      }
    };
  },
};
